import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
  useRef,
  useEffect,
  useMemo,
} from 'react';

import classNames from 'classnames';
import ReactModal from 'react-modal';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { Select } from 'src/common/components/Select';
import { OrbitIcons } from 'src/common/constants.';

import {
  createDidRouting,
  updateDidRouting,
  fetchReadyFreeSwitchGroups,
  fetchAccounts,
} from '../api';
import type {
  DidRouting,
  CreateDidRoutingPayload,
  UpdateDidRoutingPayload,
} from '../api/didRouting';
import type { FreeSwitchGroup } from '../api/freeswitchGroups';
import type { InterstellarAccount } from '../api/types';

/** Extract IP address from a FreeSWITCH URI like "sip:10.0.131.86:5080" */
const extractIpFromUri = (uri: string): string => {
  const ipRegex = /(\d+\.\d+\.\d+\.\d+)/;
  const match = ipRegex.exec(uri);
  return match ? match[1] : uri;
};

export interface DidRoutingModalRef {
  showCreate: () => void;
  showEdit: (route: DidRouting) => void;
  hide: () => void;
}

interface DidRoutingModalProps {
  onSaved: () => void;
}

type ModalMode = 'create' | 'edit';

export const DidRoutingModal = forwardRef(
  ({ onSaved }: DidRoutingModalProps, ref: ForwardedRef<DidRoutingModalRef>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<ModalMode>('create');
    const [editingRoute, setEditingRoute] = useState<DidRouting | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // FreeSWITCH groups for dropdown
    const [fsGroups, setFsGroups] = useState<FreeSwitchGroup[]>([]);
    const [loadingFsGroups, setLoadingFsGroups] = useState(false);

    // Accounts for dropdown
    const [accounts, setAccounts] = useState<InterstellarAccount[]>([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    // Form fields
    const [did, setDid] = useState('');
    const [freeswitchIp, setFreeswitchIp] = useState('');
    const [freeswitchPort, setFreeswitchPort] = useState('5080');
    const [accountId, setAccountId] = useState<number | null>(null);
    const [carrier, setCarrier] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    // Load FreeSWITCH groups and accounts when modal opens
    useEffect(() => {
      if (isOpen) {
        setLoadingFsGroups(true);
        fetchReadyFreeSwitchGroups()
          .then((groups) => setFsGroups(groups))
          .catch(() => Toast.error('Error al cargar los FreeSWITCH'))
          .finally(() => setLoadingFsGroups(false));

        setLoadingAccounts(true);
        fetchAccounts()
          .then((accts) => setAccounts(accts))
          .catch(() => Toast.error('Error al cargar las cuentas'))
          .finally(() => setLoadingAccounts(false));
      }
    }, [isOpen]);

    // Transform FreeSWITCH groups to select options with IP extracted
    const fsGroupOptions = useMemo(() => {
      const options = fsGroups.map((group) => {
        const ip = extractIpFromUri(group.freeswitch_uri);
        return { ip, label: `${group.name} (${ip})` };
      });
      // Add current IP as option if editing and not in the list
      const ipNotInList = !options.some((opt) => opt.ip === freeswitchIp);
      if (mode === 'edit' && freeswitchIp && ipNotInList) {
        options.push({ ip: freeswitchIp, label: `${freeswitchIp} (actual)` });
      }
      return options;
    }, [fsGroups, mode, freeswitchIp]);

    const resetForm = (): void => {
      setDid('');
      setFreeswitchIp('');
      setFreeswitchPort('5080');
      setAccountId(null);
      setCarrier('');
      setError(null);
      setEditingRoute(null);
    };

    const handleClose = (): void => {
      setIsOpen(false);
      resetForm();
    };

    useImperativeHandle(ref, () => ({
      showCreate: () => {
        resetForm();
        setMode('create');
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      },
      showEdit: (route: DidRouting) => {
        setMode('edit');
        setEditingRoute(route);
        setDid(route.did);
        setFreeswitchIp(route.freeswitch_ip);
        setFreeswitchPort(String(route.freeswitch_port));
        setAccountId(route.account_id);
        setCarrier(route.carrier || '');
        setError(null);
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      },
      hide: handleClose,
    }));

    const handleSubmit = async (): Promise<void> => {
      if (mode === 'create' && !did.trim()) {
        setError('El DID es requerido');
        return;
      }
      if (!freeswitchIp.trim()) {
        setError('La IP de FreeSWITCH es requerida');
        return;
      }

      const portNum = parseInt(freeswitchPort, 10);
      if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
        setError('El puerto debe ser un número entre 1 y 65535');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        if (mode === 'create') {
          const payload: CreateDidRoutingPayload = {
            did: did.trim(),
            freeswitch_ip: freeswitchIp.trim(),
            freeswitch_port: portNum,
            account_id: accountId,
            carrier: carrier.trim() || null,
          };
          await createDidRouting(payload);
          Toast.success('Ruta DID creada exitosamente');
        } else if (editingRoute) {
          const payload: UpdateDidRoutingPayload = {
            freeswitch_ip: freeswitchIp.trim(),
            freeswitch_port: portNum,
            account_id: accountId,
            carrier: carrier.trim() || null,
          };
          await updateDidRouting(editingRoute.id, payload);
          Toast.success('Ruta DID actualizada exitosamente');
        }
        onSaved();
        handleClose();
      } catch (err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        const detail = axiosErr?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Error al guardar la ruta DID');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <ReactModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className="modal modal-md"
        overlayClassName="overlay"
        appElement={document.querySelector('#root') as HTMLElement}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-black-1.0">
              {mode === 'create' ? 'Nueva Ruta DID' : 'Editar Ruta DID'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="hover:text-gray-600 text-gray-400"
            >
              <Icon name={OrbitIcons.X} size={20} />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 p-3 text-sm text-red-800 border border-red-200 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-black-1.0">
              DID (número de teléfono) <span className="text-red-500">*</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              value={did}
              onChange={(e) => setDid(e.target.value)}
              placeholder="+17875551234"
              disabled={mode === 'edit' || isSubmitting}
              className={classNames(
                'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                'border-white-2.0 bg-white-0.0',
                mode === 'edit' && 'bg-gray-50 cursor-not-allowed'
              )}
            />
            {mode === 'edit' && (
              <p className="mt-1 text-xs text-gray-2.0">El DID no puede ser modificado</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-black-1.0">
              FreeSWITCH <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <Select
                options={fsGroupOptions}
                fieldId="ip"
                fieldLabel="label"
                value={freeswitchIp}
                onChange={(opt: unknown) => {
                  const group = opt as { ip: string; label: string } | null;
                  setFreeswitchIp(group?.ip ?? '');
                }}
                placeholder={loadingFsGroups ? 'Cargando...' : 'Seleccionar FreeSWITCH...'}
                isDisabled={isSubmitting || loadingFsGroups}
                isLoading={loadingFsGroups}
                menuPortalTarget={document.body}
              />
            </div>
            {freeswitchIp && <p className="mt-1 text-xs text-gray-2.0">IP: {freeswitchIp}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-black-1.0">Puerto</label>
            <input
              type="number"
              value={freeswitchPort}
              onChange={(e) => setFreeswitchPort(e.target.value)}
              placeholder="5080"
              disabled={isSubmitting}
              className={classNames(
                'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                'border-white-2.0 bg-white-0.0'
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black-1.0">Account (opcional)</label>
            <div className="mt-1">
              <Select
                options={accounts}
                fieldId="id"
                fieldLabel="name"
                value={accountId}
                onChange={(opt: unknown) => {
                  const account = opt as InterstellarAccount | null;
                  setAccountId(account?.id ?? null);
                }}
                placeholder={loadingAccounts ? 'Cargando...' : 'Seleccionar cuenta...'}
                isClearable
                isDisabled={isSubmitting || loadingAccounts}
                isLoading={loadingAccounts}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black-1.0">Carrier (opcional)</label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={isSubmitting}
              className={classNames(
                'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:border-primary-2.0',
                'border-white-2.0 bg-white-0.0'
              )}
            >
              <option value="">Seleccionar...</option>
              <option value="twilio">Twilio</option>
              <option value="bandwidth">Bandwidth</option>
              <option value="telnyx">Telnyx</option>
              <option value="sinch">Sinch</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="btn btn-ghost"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn btn-primary flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="border-t-transparent animate-spin w-4 h-4 border-2 border-white rounded-full" />
                  Guardando...
                </>
              ) : (
                <>{mode === 'create' ? 'Crear' : 'Guardar'}</>
              )}
            </button>
          </div>
        </div>
      </ReactModal>
    );
  }
);
